'use client';

import { type FC, useEffect, useRef, useState } from 'react';
import { App } from '@/app/components/icons/App';
import { BaseLineLanguage } from '@/app/components/icons/BaseLineLanguage';
import { BaseLineList } from '@/app/components/icons/BaseLineList';
import { LoginWhite } from '@/app/components/icons/LoginWhite';
import { LogoDark } from '@/app/components/icons/LogoDark';
import { ClientLink } from '@/app/components/shared/ClientLink';
import HomeMenu from '@/app/components/shared/HomeMenu';
import { ImageWithPlaceholder } from '@/app/components/shared/ImageWithPlaceholder';
import { GLOBAL_LANGUAGES_CONFIG, QRCODE_URL, SITE } from '@/app/constants';
import { useGlobalModal, useIsScreen } from '@/app/hooks';
import type { SupportedLanguage } from '@/app/types';
import { cn, reportEvent, setRegisterPage } from '@/app/utils';
import { openOrRedirect } from '@/app/utils/client';
import { getLocalizedHref, useLocale, usePathname, useRouter } from '@/i18n/routing';

export interface TabChildConfig {
  label: string;
  key: string;
  href: string;
}

export interface TabConfig {
  label: string;
  key: string;
  href: string;
  icon?: string;
  children?: Array<TabChildConfig | false>;
}

export interface WelcomeHeaderClientProps {
  className?: string;
  theme?: 'light' | 'dark' | 'transparent';
  scrollThreshold?: number | string;
  disableScrollEffect?: boolean;
  showLanguageOption?: SupportedLanguage[];
  tabsConfig: Array<TabConfig | false>;
  i18n: {
    language: string;
    appAction: string;
    navHome: string;
  };
}

export const WelcomeHeaderClient: FC<WelcomeHeaderClientProps> = ({
  className,
  theme = 'dark',
  scrollThreshold = '70%',
  disableScrollEffect = false,
  showLanguageOption = GLOBAL_LANGUAGES_CONFIG.map((item) => item.value),
  tabsConfig,
  i18n,
}) => {
  const pathname = usePathname();
  const locale = useLocale();
  const router = useRouter();
  const { open: openDownloadAppDialog } = useGlobalModal('downloadAppDialog');
  const { isMdScreen, matchList } = useIsScreen();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('');
  const [menuHeight, setMenuHeight] = useState(0);
  const [showMenu, setShowMenu] = useState(false);
  const [languageNavOpen, setLanguageNavOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const hasReportedEarnEntranceExposeRef = useRef(false);

  const getActualScrollThreshold = () => {
    if (typeof scrollThreshold === 'string' && scrollThreshold.endsWith('%')) {
      const percentage = Number.parseFloat(scrollThreshold) / 100;
      return window.innerHeight * percentage;
    }
    return typeof scrollThreshold === 'number' ? scrollThreshold : window.innerHeight * 0.7;
  };

  useEffect(() => {
    if (languageNavOpen) {
      setSelectedLanguage(locale as SupportedLanguage);
    }
  }, [languageNavOpen, locale]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const menu = document.getElementById('dropdown-menu');
      if (menu && !menu.contains(target)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (activeTab) {
      const dropdownElement = document.querySelector(`.${activeTab}-dropdown`);
      if (dropdownElement) {
        const height = dropdownElement.getBoundingClientRect().height;
        setMenuHeight(height);
      }
    }

    if (languageNavOpen) {
      const dropdownElement = document.querySelector('.dropdown-language-container');
      if (dropdownElement) {
        const height = dropdownElement.getBoundingClientRect().height;
        setMenuHeight(height);
      }
    }

    return () => setMenuHeight(0);
  }, [activeTab, languageNavOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const threshold = getActualScrollThreshold();
      setIsScrolled(scrollPosition > threshold);
    };

    const handleResize = () => handleScroll();

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [scrollThreshold]);

  const handleLanguageChange = (language: SupportedLanguage) => {
    router.replace(pathname, { locale: language });
    setRegisterPage({ language });
    setLanguageNavOpen(false);
  };

  const visibleTabsConfig = tabsConfig.filter(Boolean);
  const shouldShowEarnEntrance = visibleTabsConfig.some((tab) => tab && tab.key === 'viduEarn');
  const isScreenReady = matchList.length > 0;

  useEffect(() => {
    if (!isScreenReady || isMdScreen || !shouldShowEarnEntrance || hasReportedEarnEntranceExposeRef.current) return;

    reportEvent('earn_home_entrance_expose', { from_source: 'official_web' });
    hasReportedEarnEntranceExposeRef.current = true;
  }, [isMdScreen, isScreenReady, shouldShowEarnEntrance]);

  return (
    <header
      className={cn(
        'fixed z-20 flex w-full items-start justify-between px-6 py-4 text-[14px] duration-200 data-[open=true]:bg-[rgba(19,12,20,0.8)] md:items-center md:px-3 md:py-3 lg:px-0 lg:py-[18px]',
        theme === 'light' && 'bg-[#FFFFFF29] text-black/60',
        theme === 'dark' &&
          (disableScrollEffect
            ? 'bg-black text-[#FFFFFF99]'
            : 'bg-[linear-gradient(180deg,rgba(0,0,0,0.32)0%,rgba(0,0,0,0)100%)] text-[#FFFFFF99]'),
        theme === 'transparent' && 'bg-[linear-gradient(180deg,rgba(0,0,0,0.5)0%,rgba(0,0,0,0)100%)] text-white',
        className,
        !disableScrollEffect && isScrolled
          ? theme === 'light'
            ? 'bg-white backdrop-blur-none'
            : 'bg-system-black56 backdrop-blur-[20px] md:bg-system-black80 md:backdrop-blur-none'
          : '',
        !disableScrollEffect && (activeTab || languageNavOpen)
          ? theme === 'light'
            ? 'bg-white backdrop-blur-none'
            : 'bg-system-black56 backdrop-blur-[20px]'
          : '',
      )}
      style={activeTab || languageNavOpen ? { height: `${menuHeight + 80}px` } : {}}
    >
      {/* PC 端 */}
      <div className="inline-flex h-11 flex-1 items-center justify-start gap-8 font-bold text-[14px] leading-[24px] lg:gap-3">
        <ClientLink href="/" aria-label="logo" className="mr-6">
          {theme === 'light' ? (
            <LogoDark className="mt-[-6px] text-[24px]" />
          ) : (
            <LoginWhite className="mt-[-6px] text-[24px] text-white" />
          )}
        </ClientLink>
        {visibleTabsConfig.map(
          (tab) =>
            tab && (
              <div key={tab.key} className="relative flex flex-col md:hidden">
                <ClientLink
                  key={`${tab.key}-header`}
                  href={tab.href}
                  className={cn(
                    'group/tab lg:px-0',
                    theme === 'light' ? 'hover:text-black' : 'hover:text-white',
                    tab.key === 'community' && SITE === 'china' && 'group relative',
                  )}
                  aria-label={tab.label}
                >
                  <div
                    className={cn('relative whitespace-nowrap px-4 py-2 font-normal leading-[22px] lg:px-3')}
                    onMouseLeave={() => {
                      setActiveTab('');
                    }}
                    onClick={(e) => {
                      if (tab.href) {
                        e.preventDefault();
                        reportEvent('element_click', {
                          element_name: `${tab.key}`,
                        });
                        if (tab.key === 'viduEarn') {
                          reportEvent('earn_home_entrance_click', { from_source: 'official_web' });
                        }
                        openOrRedirect(getLocalizedHref(tab.href, locale));
                      }
                    }}
                    onMouseEnter={() => {
                      if (tab.children && tab.children.length > 0 && activeTab !== tab.key) {
                        setActiveTab(tab.key);
                      } else {
                        setActiveTab('');
                      }
                    }}
                  >
                    {tab.label}
                    {/* hover 下划线：从中间向两侧展开 */}
                    <span
                      className={cn(
                        'absolute inset-x-4 bottom-0 h-[1.5px] origin-center scale-x-0 rounded-full transition-transform duration-300 ease-out group-hover/tab:scale-x-100 lg:inset-x-3',
                        theme === 'light' ? 'bg-black' : 'bg-white',
                      )}
                    />
                  </div>
                  {tab.key === 'community' && SITE === 'china' ? (
                    <div className="absolute top-full left-[-70px] z-10 hidden pt-2 group-hover:block">
                      <div
                        className={cn(
                          'h-[248px] w-[216px] rounded-12 border px-2 py-3',
                          theme === 'light' ? 'border-gray-200 bg-white' : 'border-system-white12 bg-system-bg02',
                        )}
                      >
                        <div className="mx-auto h-[200px] w-[200px]">
                          <ImageWithPlaceholder
                            src={QRCODE_URL}
                            alt="微信群二维码"
                            className="h-full w-full rounded-6 bg-[#3C4352]"
                          />
                        </div>
                        <p
                          className={cn(
                            'mt-2 text-center font-[400] text-xs tracking-[0]',
                            theme === 'light' ? 'text-gray-500' : 'text-system-white64',
                          )}
                        >
                          加入讨论群，了解最新动态
                        </p>
                      </div>
                    </div>
                  ) : null}
                </ClientLink>
                {tab.children && (
                  <div
                    key={`${tab.key}-dropdown`}
                    className={cn(
                      'absolute top-full left-0 z-10 flex min-w-[150px] flex-col rounded-lg pt-4',
                      `${tab.key}-dropdown`,
                      activeTab === tab.key ? 'opacity-100 duration-200' : 'pointer-events-none opacity-0 duration-200',
                    )}
                    onMouseEnter={() => {
                      if (tab.children && tab.children.length > 0 && activeTab) {
                        setActiveTab(tab.key);
                      }
                    }}
                    onMouseLeave={() => {
                      setActiveTab('');
                    }}
                  >
                    {tab.children.map(
                      (child) =>
                        child && (
                          <div key={`${tab.key}-${child.key}-child`} className="pl-3">
                            {(child.key === 'character2video' ||
                              child.key === 'img2video' ||
                              child.key === 'text2video') &&
                            SITE === 'china' ? (
                              <ClientLink
                                href={child.href}
                                className={cn(
                                  'flex cursor-pointer items-center whitespace-nowrap rounded py-4 font-normal text-sm',
                                  theme === 'light' ? 'hover:text-black' : 'hover:text-white',
                                )}
                                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                                  e.preventDefault();
                                  reportEvent('element_click', {
                                    element_name: child.key,
                                  });
                                  router.push(child.href);
                                  setActiveTab('');
                                }}
                                aria-label={child.label}
                              >
                                {child.label}
                              </ClientLink>
                            ) : (
                              <ClientLink
                                href={child.href}
                                target="_blank"
                                className={cn(
                                  'flex cursor-pointer items-center whitespace-nowrap rounded py-4 font-normal text-sm',
                                  theme === 'light' ? 'hover:text-black' : 'hover:text-white',
                                )}
                                onClick={() => {
                                  if (activeTab) {
                                    reportEvent('element_click', {
                                      element_name: child.key,
                                    });
                                    setActiveTab('');
                                  }
                                }}
                                aria-label={child.label}
                              >
                                {child.label}
                              </ClientLink>
                            )}
                          </div>
                        ),
                    )}
                  </div>
                )}
              </div>
            ),
        )}
      </div>
      <div className="flex h-11 cursor-pointer items-center justify-end gap-4 xxl:gap-10 md:hidden lg:gap-4 xl:gap-8">
        <div
          className="relative flex h-11 items-center justify-center gap-2"
          onMouseEnter={() => setLanguageNavOpen(true)}
          onMouseLeave={() => {
            setLanguageNavOpen(false);
          }}
        >
          {showLanguageOption.length > 0 && SITE === 'global' && (
            <>
              <BaseLineLanguage className={cn('size-[22px] p-0.5', theme === 'light' ? 'text-black' : 'text-white')} />
              {languageNavOpen && (
                <div
                  className={cn(
                    'dropdown-language-container absolute top-full left-0 z-50 flex min-w-[150px] cursor-pointer flex-col rounded-lg',
                    languageNavOpen ? 'opacity-100 duration-200' : 'opacity-0 duration-200',
                  )}
                  onMouseEnter={() => {
                    setLanguageNavOpen(true);
                  }}
                  onMouseLeave={() => {
                    setLanguageNavOpen(false);
                  }}
                >
                  {GLOBAL_LANGUAGES_CONFIG.filter(({ value }) => showLanguageOption.includes(value)).map(
                    ({ value, label }) => (
                      <div
                        key={value}
                        className={cn(
                          'whitespace-nowrap rounded py-4 text-sm',
                          theme === 'light' ? 'hover:text-black' : 'hover:text-white',
                          selectedLanguage === value &&
                            (theme === 'light' ? 'font-medium text-black' : 'font-medium text-white'),
                        )}
                        onClick={() => handleLanguageChange(value)}
                      >
                        {label}
                      </div>
                    ),
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* 添加APP下载按钮 */}
        <button
          type="button"
          onClick={() => {
            openDownloadAppDialog({ fromPage: 'landing_page' });
            reportEvent('get_app_click', {
              click_position: 'landing_page',
            });
          }}
          className={cn(
            'group inline-flex h-11 items-center justify-center rounded-full px-6 font-medium text-[14px] leading-5 backdrop-blur-[20px] hover:opacity-90',
            theme === 'light' ? 'bg-black/5 text-black' : 'text-white',
          )}
        >
          <App className={cn('mr-1 flex-shrink-0 text-lg', theme === 'light' ? 'text-black' : 'text-white')} />
          <span className="whitespace-nowrap lg:hidden">{i18n.appAction}</span>
        </button>

        <ClientLink
          href="/home"
          className={cn(
            'box-border inline-flex h-11 cursor-pointer items-center justify-center text-nowrap rounded-full border-[1px] font-semibold text-[14px] leading-[28px] backdrop-blur-[10px]',
            theme === 'light'
              ? 'border-black bg-black px-8 text-white hover:bg-black/80'
              : 'border-system-white/30 bg-system-black px-8 text-white',
            isScrolled && !theme && 'border-[#0086ff] bg-[#0086ff]',
          )}
          aria-label="trynow"
          onClick={() => {
            reportEvent('element_click', {
              element_name: 'landing - trynow',
            });
          }}
        >
          {i18n.navHome}
        </ClientLink>
      </div>

      {/* 移动端  */}
      <div className="hidden space-x-4 text-center text-base md:block">
        <BaseLineList
          className={cn('size-6', theme === 'light' ? 'text-black' : 'text-white')}
          onClick={() => setShowMenu(true)}
        />
      </div>
      {showMenu && (
        <div className="fixed top-0 left-0 h-screen w-full bg-[rgba(0,0,0,0.6)] backdrop-blur-[10px]">
          <HomeMenu onClose={() => setShowMenu(false)} showMenu={showMenu} />
        </div>
      )}
    </header>
  );
};
